(() => {
  'use strict';
  if (window.limartePaintedVersionsLoaded) return;
  window.limartePaintedVersionsLoaded = true;
  const instances = new WeakMap();
  const shuffle = values => {
    const list = [...values];
    for (let i = list.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [list[i], list[j]] = [list[j], list[i]];
    }
    return list;
  };
  function init(root) {
    if (instances.has(root)) return;
    const poolTemplate = root.querySelector('[data-painted-pool]');
    const row = root.querySelector('[data-painted-row]');
    if (!poolTemplate || !row) return;
    const refresh = root.querySelector('[data-painted-refresh]');
    const pause = root.querySelector('[data-painted-pause]');
    const status = root.querySelector('[data-painted-status]');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const hover = matchMedia('(hover: hover)');
    const pool = new Map();
    let bag = [], busy = false, paused = reduced.matches, disposed = false;
    const interval = Math.max(5000, Number(root.dataset.interval) || 8000);
    const add = template => template.content.querySelectorAll('[data-painted-handle]').forEach(card => {
      if (!pool.has(card.dataset.paintedHandle)) pool.set(card.dataset.paintedHandle, card);
    });
    function controls() {
      const canChange = pool.size > 5;
      refresh.hidden = !canChange;
      pause.hidden = !canChange || reduced.matches;
      pause.setAttribute('aria-pressed', String(paused));
      pause.setAttribute('aria-label', paused ? 'Resume automatic product changes' : 'Pause automatic product changes');
      pause.title = pause.getAttribute('aria-label');
      pause.querySelector('[data-painted-pause-icon]').textContent = paused ? '▶' : 'Ⅱ';
    }
    function selection(avoidCurrent) {
      const count = Math.min(5, pool.size);
      const current = new Set(avoidCurrent ? [...row.querySelectorAll('[data-painted-handle]')].map(card => card.dataset.paintedHandle) : []);
      const chosen = [];
      if (!bag.length) bag = shuffle([...pool.keys()]);
      for (let i = 0; i < count; i++) {
        let index = bag.findIndex(handle => !current.has(handle) && !chosen.includes(handle));
        if (index < 0) {
          const candidates = [...pool.keys()].filter(handle => !current.has(handle) && !chosen.includes(handle));
          if (candidates.length) { bag = shuffle(candidates); index = 0; }
          else {
            const candidates = [...pool.keys()].filter(handle => !chosen.includes(handle));
            bag = shuffle(candidates); index = 0;
          }
        }
        chosen.push(bag.splice(index, 1)[0]);
      }
      return chosen.map(handle => pool.get(handle).cloneNode(true));
    }
    async function change(animate = true, manual = false) {
      if (busy || disposed || !pool.size) return;
      // Never replace a card while the visitor is using its link or wishlist heart.
      if (row.contains(document.activeElement)) return;
      busy = true;
      refresh.disabled = true;
      const cards = selection(animate);
      const existing = [...row.children];
      const slots = cards.map((card, index) => {
        const slot = existing[index] || document.createElement('div');
        slot.classList.add('painted-slot');
        if (!slot.isConnected) row.append(slot);
        card.classList.add('painted-incoming');
        card.setAttribute('aria-hidden', 'true');
        card.inert = true;
        slot.append(card);
        return slot;
      });
      // Decode attached responsive images before beginning the transition.
      await Promise.race([
        Promise.all(cards.map(card => { const img = card.querySelector('img'); if (!img) return; img.loading = 'eager'; return img.decode().catch(() => {}); })),
        new Promise(resolve => setTimeout(resolve, 4000))
      ]);
      if (disposed) return;
      if (row.contains(document.activeElement)) {
        cards.forEach(card => card.remove()); busy = false; refresh.disabled = false; return;
      }
      cards.forEach((card, index) => {
        card.style.transitionDelay = animate && !reduced.matches ? `${index * 65}ms` : '0ms';
        const outgoing = slots[index].querySelector('.painted-card:not(.painted-incoming)');
        if (outgoing) { outgoing.classList.add('painted-leaving'); outgoing.inert = true; outgoing.setAttribute('aria-hidden', 'true'); }
      });
      // Flush initial styles so the entering cards fade instead of flashing.
      void row.offsetWidth;
      cards.forEach(card => card.classList.add('painted-entering'));
      if (animate && !reduced.matches) await new Promise(resolve => setTimeout(resolve, 720));
      if (disposed) return;
      slots.forEach((slot, index) => {
        const card = cards[index];
        card.classList.remove('painted-incoming', 'painted-entering');
        card.removeAttribute('aria-hidden'); card.inert = false;
        card.style.transitionDelay = '';
        slot.replaceChildren(card);
      });
      existing.slice(cards.length).forEach(slot => slot.remove());
      document.dispatchEvent(new Event('limarte:wishlist:refresh'));
      if (manual) status.textContent = 'Another random selection of hand-painted versions is ready.';
      busy = false;
      refresh.disabled = false;
    }
    async function loadMore() {
      let next = poolTemplate.dataset.nextUrl;
      const seen = new Set();
      while (next && !disposed) {
        const collectionUrl = new URL(root.dataset.collectionUrl, location.origin);
        const nextUrl = new URL(next, location.origin);
        if (collectionUrl.origin !== location.origin || nextUrl.origin !== location.origin) break;
        collectionUrl.search = nextUrl.search;
        collectionUrl.searchParams.delete('section_id');
        collectionUrl.searchParams.set('view', 'painted-products');
        const url = collectionUrl.toString();
        if (seen.has(url)) break;
        seen.add(url);
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 10000);
        try {
          const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
          if (!response.ok || disposed) break;
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const template = html.querySelector('[data-painted-pool]');
          if (!template) break;
          add(template); controls();
          next = template.dataset.nextUrl;
        } catch (_) { break; }
        finally { clearTimeout(timeout); }
      }
      // Include later collection pages in a fresh random draw.
      bag = [];
      root.dataset.paintedPoolReady = 'true';
      if (pool.size > 50 && !document.hidden) change(true);
    }
    add(poolTemplate); controls();
    change(false);
    loadMore();
    const timer = setInterval(() => {
      if (paused || reduced.matches || document.hidden || pool.size <= 5 || disposed) return;
      if (hover.matches && row.matches(':hover')) return;
      change(true);
    }, interval);
    refresh.addEventListener('click', () => change(true, true));
    pause.addEventListener('click', () => { paused = !paused; controls(); });
    const motionChange = () => { if (reduced.matches) paused = true; controls(); };
    reduced.addEventListener('change', motionChange);
    instances.set(root, () => { disposed = true; clearInterval(timer); reduced.removeEventListener('change', motionChange); });
  }
  const initAll = () => document.querySelectorAll('[data-painted-versions]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-painted-versions]').forEach(root => { instances.get(root)?.(); instances.delete(root); });
  });
})();
